import {
  type ActiveEvent,
  type Config,
  type EventConfig,
  type GameConfig,
  type GameCardConfigBase,
  GameTypes
} from "kadow-jetpack-model";
import { isPerformanceGameHiddenByDisabledMonthlyLottery } from "../../../../../utils/helpers/performanceGameAvailability";
import { buildGameThumbnailDTO } from "../../../../../utils/helpers/buildGameThumbnailDTO";
import { DEFAULT_EVENTS } from "../../../../../utils/constants";
import { type GameThumbnailDTO } from "../../../../../model/GameDTO";

export const GAME_LIST_PARAM = "r";
export const GAME_LIST_WRAP_PARAM = "f";

export const GAME_LIST_MODES = {
  TwoSections: "2",
  ScheduleIndicator: "3"
} as const;

export function isWrapEnabled(rawParam: string | null): boolean {
  return rawParam === "true";
}

export type GameListMode = "default" | "twoSections" | "scheduleIndicator";

export function resolveGameListMode(rawParam: string | null): GameListMode {
  if (rawParam === GAME_LIST_MODES.TwoSections) {
    return "twoSections";
  }
  if (rawParam === GAME_LIST_MODES.ScheduleIndicator) {
    return "scheduleIndicator";
  }
  return "default";
}

export type GameListItem =
  | { type: "game"; key: string; game: GameConfig }
  | { type: "event"; key: string; ae: ActiveEvent };

export interface GameListSection {
  key: string;
  title?: string;
  description?: string;
  items: GameListItem[];
}

/*
  Items without an explicit order (legacy configs, freshly added games/events) are
  pushed to the end while keeping their insertion order via a stable sort.
*/
export const UNORDERED_POSITION = Number.MAX_SAFE_INTEGER;

export function getGameListItemOrder(item: GameListItem): number {
  const order = item.type === "game" ? item.game.o : item.ae.o;
  return order ?? UNORDERED_POSITION;
}

/*
  Games and events share a single ordering space (the "o" property), so we sort them
  together to allow interleaving.
*/
export function orderGameListItems(items: GameListItem[]): GameListItem[] {
  return [...items].sort(
    (a, b) => getGameListItemOrder(a) - getGameListItemOrder(b)
  );
}

const PERMANENT_TS = -1;

export function isPermanentEvent(ae: ActiveEvent): boolean {
  return ae.sts === PERMANENT_TS && ae.ets === PERMANENT_TS;
}

export function buildGameItem(game: GameConfig): GameListItem {
  return { type: "game", key: `game-${game.name}`, game };
}

export function buildEventItem(ae: ActiveEvent): GameListItem {
  return {
    type: "event",
    key: `event-${ae.id}-${ae.gameType}-${ae.sts}-${ae.ets}`,
    ae
  };
}

export interface EventCardData {
  eventConfig: EventConfig;
  cardConfig: GameCardConfigBase;
  isOngoing: boolean;
}

export function getEventCardData(
  ae: ActiveEvent,
  events: EventConfig[] | undefined | null,
  config: Config | null,
  nowTs: number
): EventCardData | null {
  if (!ae.gameType) {
    return null;
  }

  const eventConfig = events?.find((e) => e._id === ae.id);

  if (!eventConfig) {
    return null;
  }

  const skin = eventConfig.skins.find((s) => s.game === ae.gameType);

  if (!skin) {
    return null;
  }

  const gameType = ae.gameType as unknown as GameTypes;

  if (
    gameType &&
    isPerformanceGameHiddenByDisabledMonthlyLottery(gameType, config)
  ) {
    return null;
  }

  const isOngoing =
    !!eventConfig.permanent || (nowTs >= ae.sts && nowTs <= ae.ets);

  return { eventConfig, cardConfig: skin.gameCardConfig, isOngoing };
}

export function getGameThumbnail(game: GameConfig): GameThumbnailDTO | null {
  const res: GameThumbnailDTO[] = [];
  buildGameThumbnailDTO(res, DEFAULT_EVENTS, "default", game.name);
  return res[0] || null;
}
